import React, { FC, useEffect, useState } from 'react'
import { useFormik } from 'formik'
import { useTranslation } from 'react-i18next'
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import { BackArrow, Button, CheckBox, Input, Select, TopProgress } from '../../../components'
import preorderHardwareService, { PreorderHardware, PreorderHardwareType } from '../../../api/preorderHardwareService'
import { useUi } from '../../../context/ui.context'
import { usePageTitle } from '../../../context/navigationContext'
import { extractErrorMessage } from '../../../utils/error-extractor'
import { DetailContainer, DetailHeader, DetailTitle } from '../styles'
import {
  diskTypes, hardwareConfig, hardwarePath, hardwarePayload, hardwareSchema,
  initialHardwareValues, isHardwareType,
} from './config'
import { ErrorMessage, FormActions, FormCard, FormGrid } from './styles'

const HardwareForm: FC<{ type: PreorderHardwareType; id?: string }> = ({ type, id }) => {
  const [t] = useTranslation()
  const { viewAlert } = useUi()
  const navigate = useNavigate()
  const location = useLocation()
  const [item, setItem] = useState<PreorderHardware>()
  const [loading, setLoading] = useState(Boolean(id))
  const [loadError, setLoadError] = useState('')
  const title = t(hardwareConfig[type][id ? 'edit' : 'create'])
  const returnParams = new URLSearchParams(location.state?.returnSearch || '')
  returnParams.set('tab', type)
  const returnPath = `${hardwarePath}?${returnParams}`
  usePageTitle(title)

  useEffect(() => {
    if (!id) return
    const controller = new AbortController()
    let retry: number | undefined
    const load = async () => {
      try {
        const { data } = await preorderHardwareService.get(type, id, controller.signal)
        if (controller.signal.aborted) return
        setItem(data)
        setLoadError('')
      } catch (error: any) {
        if (controller.signal.aborted) return
        setLoadError(extractErrorMessage(error?.response?.data) || t('PREORDER_LOAD_FAILED'))
        retry = window.setTimeout(load, 10000)
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    }
    load()
    return () => {
      window.clearTimeout(retry)
      controller.abort()
    }
  }, [id, type, t])

  const formik = useFormik({
    initialValues: initialHardwareValues(item),
    enableReinitialize: true,
    validationSchema: hardwareSchema(type, t),
    onSubmit: async (values, helpers) => {
      helpers.setStatus(undefined)
      try {
        const payload = hardwarePayload(type, values)
        if (id) await preorderHardwareService.update(type, id, payload)
        else await preorderHardwareService.create(type, payload)
        viewAlert({ severity: 'success', message: t(id ? 'PREORDER_UPDATED' : 'PREORDER_CREATED') })
        // New items may not match the filters on the previous list.
        navigate(id ? returnPath : `${hardwarePath}?tab=${type}`, { replace: true })
      } catch (error: any) {
        const data = error?.response?.data
        if (data && typeof data === 'object') {
          Object.entries(data).forEach(([field, messages]) => {
            if (field in values) {
              helpers.setFieldError(field, Array.isArray(messages) ? messages.join(' ') : String(messages))
            }
          })
        }
        helpers.setStatus(extractErrorMessage(data) || t('ERROR_OCCURRED'))
      }
    },
  })
  const formikProps = {
    values: formik.values,
    errors: formik.errors,
    touched: formik.touched,
    handleChange: formik.handleChange,
    handleBlur: formik.handleBlur,
    disabled: formik.isSubmitting,
  }

  return (
    <DetailContainer>
      <BackArrow onClick={() => navigate(returnPath)} fallbackPath={returnPath} fallbackTitle={String(t('PREORDER_HARDWARE'))} />
      <DetailHeader><DetailTitle>{title}</DetailTitle></DetailHeader>
      {loading ? <TopProgress loading /> : loadError ? (
        <ErrorMessage role="alert">{loadError}</ErrorMessage>
      ) : (
        <FormCard>
          <form onSubmit={formik.handleSubmit} noValidate>
            <FormGrid>
              <Input {...formikProps} fieldName="name" value={formik.values.name} title={t('NAME')} placeholder={t('NAME')} required />
              <Input {...formikProps} fieldName="model" value={formik.values.model} title={t('PREORDER_MODEL')} placeholder={t('PREORDER_MODEL')} required />
              {type === 'disks' && (
                <Select
                  title={t('DEDICATED_DISK_TYPE')}
                  fieldName="disk_type"
                  required
                  disabled={formik.isSubmitting}
                  selectedValue={formik.values.disk_type}
                  data={diskTypes.map((value) => ({ value, label: value }))}
                  onChange={(value) => formik.setFieldValue('disk_type', value)}
                  errors={formik.errors}
                  touched={formik.touched}
                />
              )}
              {hardwareConfig[type].fields.map((field) => (
                <Input
                  key={field.key}
                  {...formikProps}
                  fieldName={field.key}
                  title={t(field.label)}
                  placeholder={t(field.label)}
                  type="number"
                  value={formik.values[field.key]}
                  required
                />
              ))}
              <div style={{ gridColumn: '1 / -1' }}>
                <Input {...formikProps} fieldName="description" title={t('DESCRIPTION')} placeholder={t('DESCRIPTION')} isTextArea value={formik.values.description} />
              </div>
            </FormGrid>
            <CheckBox
              title={t('ACTIVE')}
              value={formik.values.is_active}
              disabled={formik.isSubmitting}
              onChange={() => { if (!formik.isSubmitting) formik.setFieldValue('is_active', !formik.values.is_active) }}
            />
            {formik.status && <ErrorMessage role="alert">{formik.status}</ErrorMessage>}
            <FormActions>
              <Button type="submit" title={t(id ? 'SAVE' : 'CREATE')} variant="primary" size="display" loading={formik.isSubmitting} />
              <Button title={t('CANCEL')} variant="stroke" size="display" disabled={formik.isSubmitting} onClick={() => navigate(returnPath)} />
            </FormActions>
          </form>
        </FormCard>
      )}
    </DetailContainer>
  )
}

const PreorderHardwareForm: FC = () => {
  const { hardwareType, hardwareId } = useParams()
  if (!isHardwareType(hardwareType)) return <Navigate replace to={hardwarePath} />
  return <HardwareForm key={`${hardwareType}/${hardwareId || 'create'}`} type={hardwareType} id={hardwareId} />
}

export default PreorderHardwareForm
